import { z } from 'zod';

import { isoDateTimeSchema, slugSchema } from '../common/primitives';

/**
 * Ergebnis der Systemprüfung eines Worker-Nodes ("Preflight").
 *
 * Warum das ein geteilter Vertrag ist und keine reine Worker-Interna: Der
 * Master zeigt den Bericht in der Flotten-Übersicht an. Wenn ein Node nicht
 * platziert wird, soll man auf einen Blick sehen, ob Docker fehlt, die Platte
 * voll ist oder das Datenverzeichnis nicht schreibbar ist — statt sich per SSH
 * durch Logs zu graben.
 *
 * Der Agent führt diese Prüfungen beim Start aus und danach zyklisch. Ein
 * `fail` verhindert das Enrollment und nimmt den Node aus der Platzierung
 * (`REJECTION_REASONS.preflightFailed`). Lieber ein Node, der sich selbst für
 * untauglich erklärt, als ein Node, der eine Bestellung annimmt und mitten im
 * Provisioning scheitert.
 *
 * **Hier liegen auch die Plattformdienste eines Workers** — der lokale Traefik
 * und seit M4a der geteilte MariaDB-Server. Sie gehören ausdrücklich nicht ins
 * Heartbeat-Inventar: Das Inventar ist der Ist-Zustand zu einem Soll, das der
 * Master in seiner Datenbank hält, und für diese beiden Dienste hält er keines —
 * sie stammen aus `infra/worker/docker-compose.yml`. Ein Ist ohne Soll im
 * Inventar wäre genau die Vermischung, die ADR 0005 beseitigt hat, und der
 * Reconciler hätte ohnehin kein Verb, mit dem er darauf antworten könnte.
 */

export const PREFLIGHT_STATUS = {
  /** Anforderung erfüllt. */
  pass: 'pass',
  /** Betrieb möglich, aber nicht empfohlen (z. B. Platte zu 85 % voll). */
  warn: 'warn',
  /** Harte Verletzung — der Node darf keine Sites aufnehmen. */
  fail: 'fail',
  /** Prüfung war in dieser Umgebung nicht durchführbar (z. B. macOS-Entwicklung). */
  skipped: 'skipped',
} as const;

export type PreflightStatus = (typeof PREFLIGHT_STATUS)[keyof typeof PREFLIGHT_STATUS];

export const preflightStatusSchema = z.enum([
  PREFLIGHT_STATUS.pass,
  PREFLIGHT_STATUS.warn,
  PREFLIGHT_STATUS.fail,
  PREFLIGHT_STATUS.skipped,
]);

/**
 * Stabile Bezeichner der einzelnen Prüfungen.
 * Werden im Frontend zur Übersetzung und Verlinkung von Runbooks genutzt und
 * sind damit Teil des Vertrags.
 */
export const PREFLIGHT_CHECKS = {
  dockerReachable: 'docker-reachable',
  dockerVersion: 'docker-version',
  dockerApiPermissions: 'docker-api-permissions',
  dataDirectoryWritable: 'data-directory-writable',
  diskSpace: 'disk-space',
  memory: 'memory',
  cpuCores: 'cpu-cores',
  masterReachable: 'master-reachable',
  nodeRuntime: 'node-runtime',
  /** Existiert das geteilte Netz, in dem der lokale Traefik lauscht? */
  ingressNetwork: 'ingress-network',
  /** Läuft der lokale Reverse-Proxy und antwortet er auf dem Ingress-Port? */
  ingressProxy: 'ingress-proxy',
  /** Existiert das geteilte Netz, in dem der MariaDB-Server lauscht? */
  databaseNetwork: 'database-network',
  /** Antwortet der geteilte MariaDB-Server auf eine echte Abfrage? */
  databaseServer: 'database-server',
  /** Antwortet der SFTP-Dienst auf seiner Verwaltungs-API? */
  sftpGateway: 'sftp-gateway',
  /**
   * Liegt das Datenverzeichnis auf einem eigenen Dateisystem mit
   * Projektquoten — und liegt `deleted/` mit darauf?
   *
   * Der Riegel hinter `nofail` in der `/etc/fstab`: Ohne ihn startete ein
   * Worker nach einem Neustart ohne eingehängtes Abbild anstandslos durch, und
   * die Container liefen auf leere Verzeichnisse. Der Kunde sähe eine leere
   * Website, während seine Daten unversehrt im Abbild liegen.
   */
  siteQuotaMount: 'site-quota-mount',
} as const;

export type PreflightCheckId = (typeof PREFLIGHT_CHECKS)[keyof typeof PREFLIGHT_CHECKS];

/** Ergebnis einer einzelnen Prüfung. */
export const preflightCheckResultSchema = z.object({
  /** Stabiler Bezeichner, siehe `PREFLIGHT_CHECKS`. */
  id: slugSchema,
  /** Kurzer Titel auf Deutsch, z. B. "Docker-Daemon erreichbar". */
  title: z.string().min(1),
  status: preflightStatusSchema,
  /** Ergebnis in einem Satz, auf Deutsch. */
  message: z.string().min(1),
  /**
   * Konkreter Lösungshinweis bei `warn` oder `fail`.
   * Nicht optional aus Bequemlichkeit weggelassen: Eine Fehlermeldung ohne
   * Handlungsanweisung kostet den Betreiber jedes Mal dieselbe Recherche.
   */
  remediation: z.string().nullable(),
  /** Gemessener Wert und Erwartung, für die Detailansicht. */
  observed: z.string().nullable(),
  expected: z.string().nullable(),
  /** Dauer der Prüfung — macht hängende Prüfungen sichtbar. */
  durationMs: z.number().nonnegative(),
});

export type PreflightCheckResult = z.infer<typeof preflightCheckResultSchema>;

/** Gesamtbericht aller Prüfungen eines Durchlaufs. */
export const preflightReportSchema = z.object({
  executedAt: isoDateTimeSchema,
  /**
   * Schlechtester Einzelstatus des Durchlaufs.
   * `skipped` zählt dabei nicht als Verschlechterung.
   */
  overallStatus: preflightStatusSchema,
  checks: z.array(preflightCheckResultSchema),
});

export type PreflightReport = z.infer<typeof preflightReportSchema>;

/** Rangfolge der Schweregrade — höher bedeutet schlechter. */
const STATUS_SEVERITY: Record<PreflightStatus, number> = {
  [PREFLIGHT_STATUS.skipped]: 0,
  [PREFLIGHT_STATUS.pass]: 1,
  [PREFLIGHT_STATUS.warn]: 2,
  [PREFLIGHT_STATUS.fail]: 3,
};

/**
 * Ermittelt den Gesamtstatus aus Einzelergebnissen.
 *
 * Ein leerer Bericht ergibt `fail`, nicht `pass`: "Es wurde nichts geprüft" darf
 * niemals wie "alles in Ordnung" aussehen. Diese Unterscheidung ist der ganze
 * Grund, warum diese Funktion existiert und nicht inline geschrieben wird.
 */
export function determineOverallStatus(checks: readonly PreflightCheckResult[]): PreflightStatus {
  if (checks.length === 0) {
    return PREFLIGHT_STATUS.fail;
  }

  let worst: PreflightStatus = PREFLIGHT_STATUS.pass;

  for (const check of checks) {
    if (STATUS_SEVERITY[check.status] > STATUS_SEVERITY[worst]) {
      worst = check.status;
    }
  }

  return worst;
}

/** Ein Node ist betriebsbereit, solange keine Prüfung `fail` meldet. */
export function isOperational(report: PreflightReport): boolean {
  return report.overallStatus !== PREFLIGHT_STATUS.fail;
}
